/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,

	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]',
		misplacedGiftProduct: '.mm-dc-product-container[data-gift-metafield="true"]'
	},

	// sessionStorage key for free products declined by the customer
	declinedFreeProductsKey: 'mm_declined_free_products'
};

/*********************************************************************************************
 * 🎁 DECLINED FREE PRODUCTS (session-scoped)
 */
const DeclinedFreeProducts = {
	get() {
		try {
			return JSON.parse(sessionStorage.getItem(CART_CONFIG.declinedFreeProductsKey)) || [];
		} catch {
			return [];
		}
	},
	add(variantId) {
		const declined = new Set(this.get());
		declined.add(String(variantId));
		sessionStorage.setItem(CART_CONFIG.declinedFreeProductsKey, JSON.stringify([...declined]));
	},
	has(variantId) {
		return this.get().includes(String(variantId));
	},
	clear(variantId) {
		const declined = this.get().filter((id) => id !== String(variantId));
		sessionStorage.setItem(CART_CONFIG.declinedFreeProductsKey, JSON.stringify(declined));
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Use global debounce from mm_utils.js

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'cart'
	};

	return document.dispatchEvent(new CustomEvent(`cart:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const DOM = {
	get cart() { return document.querySelector(CART_CONFIG.selectors.cart); },
	get cartSection() { return document.getElementById(this.cart?.id || ''); },
	get cartProducts() { return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts); },
	get cartWrapper() { return document.querySelector(CART_CONFIG.selectors.cartWrapper); },
	get cartBubbles() { return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles); },

	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace('{{index}}', index)),

	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity)
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit('api-start', { method, path });

		const baseUrl = window.Shopify?.routes?.root || '/';
		const url = baseUrl + path;

		const isFormData = (typeof FormData !== 'undefined') && body instanceof FormData;

		const config = { method };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { 'Content-Type': 'application/json' };
		}

		if (body && method !== 'GET') {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || 'Shopify API Error');
			}

			cartEmit('api-success', { method, path, itemCount: data.item_count });
			await this.updateUI(data);
			return data;
		} catch (error) {
			cartEmit('api-error', { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Updates cart UI elements with new data
	 */
	async updateUI(data) {
		// cart/add.js doesn't return item_count (only the added line item), unlike cart.js/change.js/update.js
		let count = data.item_count;
		if (count === undefined) {
			try {
				const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
				count = cart.item_count || 0;
			} catch (error) {
				window.log('error', 'Cart', 'Failed to fetch cart item count', { error: error.message });
				count = 0;
			}
		}

		DOM.cartBubbles.forEach(bubble => {
			bubble.classList.toggle('mm-none', count === 0);
			bubble.textContent = count || '';
		});
		cartEmit('count-updated', { count });

		// Update cart content if sections exist
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement('div');
		temp.innerHTML = html;

		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);

		if (newCart && currentCart) {
			// Preserve the reassurance slider node (static content, already mounted by SplideManager — must not be replaced)
			const reaSlider = currentCart.querySelector('[data-rea-slider]');
			if (reaSlider) reaSlider.remove();

			// Remove the freshly-rendered slider from the incoming HTML too, otherwise it duplicates the preserved one below
			const newReaSlider = newCart.querySelector('[data-rea-slider]');
			if (newReaSlider) newReaSlider.remove();

			currentCart.innerHTML = newCart.innerHTML;

			// Restore the preserved reassurance slider at the end of the drawer
			if (reaSlider) currentCart.appendChild(reaSlider);

			// Reinitialize only non-rea Splide sliders (e.g. cross-sell) on the new DOM nodes
			if (typeof Splide !== 'undefined') {
				currentCart.querySelectorAll('.splide:not([data-rea-slider])').forEach(function(element) {
					if (!element._splide) {
						try {
							element._splide = new Splide(element).mount();
						} catch (error) {
							window.log('warn', 'Cart', 'Failed to initialize Splide:', { element, error });
						}
					}
				});
			}
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}
			if (typeof window.initCartQuantityListeners === 'function') {
				window.initCartQuantityListeners();
			}
			// Reinitialize promo code listeners
			if (typeof initPromoCode === 'function') {
				initPromoCode();
			}
			cartEmit('refreshed');
		}
	}
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for cart drawer
	 */
	setupFocusTrap(cartWrapper, triggerElement) {
		const focusableElements = cartWrapper.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes cart
			if (e.key === 'Escape') {
				e.preventDefault();
				this.close();
				this.focusTrap.triggerElement?.focus();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);

		window.log('info', 'Cart', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'Cart', 'Focus trap removed');
		}
	},

	async get() {
		cartEmit('fetch-start');
		const data = await CartAPI.request('GET', 'cart.js');
		cartEmit('fetched', { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Open cart
	 */
	open(triggerElement) {
		if (!cartEmit('before-open', {}, true)) return;

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper && html) {
			wrapper.classList.add('mm-dc-is-open');

			// Everything below (stop-scrolling, Lenis, inert, focus trap, the 'opened' event and
			// its cross-sell fetch listener) is deferred until the slide-in transition fully ends.
			// Running any of it earlier — even just one rAF later — puts JS work on the main thread
			// while the drawer's CSS transform transition is still running, which was dropping
			// frames and making the opening animation look janky. close() never had this problem
			// since it doesn't do this extra work right after its class toggle.
			const drawerEl = wrapper.querySelector('.mm-drawer-cart');
			const afterOpenTransition = () => {
				html.classList.add('stop-scrolling');

				// Stop Lenis smooth scrolling: its RAF loop otherwise keeps running
				// alongside the drawer's CSS transition and competes for the main thread.
				window.LenisManager?.stop();

				// Inert background content for screen readers (WCAG 2.4.3)
				document.querySelectorAll('main, header, footer').forEach(el => {
					if (!wrapper.contains(el)) el.inert = true;
				});

				// Hide overlays
				['shopify-chat', 'axeptio_overlay'].forEach(id => {
					const elem = document.getElementById(id);
					if (elem) elem.style.display = 'none';
				});

				// Fallback to active element so focus is always restored on close (WCAG 2.4.3)
				const trigger = triggerElement || document.activeElement;
				this.setupFocusTrap(wrapper, trigger);

				cartEmit('opened');
			};

			if (drawerEl) {
				drawerEl.addEventListener('transitionend', function onEnd(e) {
					if (e.propertyName !== 'transform') return;
					drawerEl.removeEventListener('transitionend', onEnd);
					afterOpenTransition();
				});
			} else {
				afterOpenTransition();
			}
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit('before-close', {}, true)) return;

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper) wrapper.classList.remove('mm-dc-is-open');
		if (html) html.classList.remove('stop-scrolling');

		// Resume Lenis smooth scrolling (mobile has no Lenis instance, hence the manual toggle above)
		window.LenisManager?.resume();
		requestAnimationFrame(() => window.LenisManager?.resize());

		// Restore background content
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);

		// Remove focus trap
		this.removeFocusTrap();

		// Show overlays
		['shopify-chat', 'axeptio_overlay'].forEach(id => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = '';
		});

		cartEmit('closed');
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {}
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll('[data-property]');
		propertyElements.forEach(el => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll('[data-variant-option]');
		variantElements.forEach(el => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach(key => {
			if (!['cartItemId', 'lineIndex', 'maxQuantity'].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;

		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		elem.textContent = newQty;

		if (!skipEvent) {
			cartEmit('quantity-changed', { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	async updateQuantity(lineIndex, newQty) {
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = itemEl?.dataset.cartItemId;

		cartEmit('quantity-sync-start', { lineIndex, quantity: newQty, variantId });

		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

			// Shopify cart/change.js requires 1-based line index and proper sections format
			const requestBody = {
				line: parseInt(lineIndex, 10), // Should be 1-based from template
				quantity: newQty
			};

			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}

			const data = await CartAPI.request('POST', 'cart/change.js', requestBody);

			await this.processFreeProducts(data);
			cartEmit('quantity-synced', { lineIndex, quantity: newQty, variantId });
			return data;
		} catch (error) {
			cartEmit('quantity-sync-error', { lineIndex, error: error.message });
			// Fallback: refresh cart to sync with server state
			this.get().catch(error => window.log('error', 'Cart', 'Failed to resync cart after quantity error', { error: error.message }));
			throw error;
		}
	},

	/**
	 * Process free products
	 * Serialized via a shared queue: concurrent callers (page load sync, add-to-cart,
	 * quantity change, promo code...) must never read the DOM and decide to add a gift
	 * at the same time, or cart/update.js can create a duplicate line for the same variant.
	 */
	_freeProductsQueue: Promise.resolve(),
	processFreeProducts(data) {
		const run = () => this._processFreeProductsSerialized(data);
		this._freeProductsQueue = this._freeProductsQueue.then(run, run);
		return this._freeProductsQueue;
	},

	async _processFreeProductsSerialized(data) {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
		if (!data.sections?.[sectionId]) return data;

		const temp = document.createElement('div');
		temp.innerHTML = data.sections[sectionId];
		const freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);

		const updates = {};
		const configuredVariantIds = new Set();

		if (freeProductsEl?.children.length) {
			Array.from(freeProductsEl.children).forEach(el => {
				const variantId = el.dataset.variantId;
				if (!variantId) {
					// Block misconfigured in the theme editor (no product selected) — skip it
					window.log('warn', 'Cart', 'Free product block has no variant ID, skipping', { productId: el.dataset.productId });
					return;
				}
				configuredVariantIds.add(variantId);

				const shouldHave = el.dataset.freeProduct === 'true';
				const hasIt = !!document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', variantId));

				if (!shouldHave) {
					// Below threshold again: forget any prior decline so it can be re-offered later
					DeclinedFreeProducts.clear(variantId);
				}

				if (hasIt && !shouldHave) {
					updates[variantId] = 0;
					cartEmit('free-product-removing', { variantId });
				} else if (!hasIt && shouldHave && !DeclinedFreeProducts.has(variantId)) {
					updates[variantId] = 1;
					cartEmit('free-product-adding', { variantId });
				}
			});
		}

		// Sécurité : un produit rendu comme cadeau (mm-dc-free-product) dont la variante n'est plus
		// celle configurée sur un bloc free_product est orphelin (ex : le marchand a changé le produit
		// offert dans l'éditeur de thème) — il n'est plus jamais reconcilié ci-dessus, donc on le retire.
		temp.querySelectorAll('.mm-dc-free-product').forEach(el => {
			const variantId = el.dataset.cartItemId;
			if (!variantId || configuredVariantIds.has(variantId)) return;
			updates[variantId] = 0;
			cartEmit('free-product-orphaned-removed', { variantId });
			window.log('warn', 'Cart', 'Orphaned free product (no longer configured on a block), removing', { variantId });
		});

		// Sécurité : un produit metafield produit_cadeau ne doit jamais rester en ligne "normale"
		// (mm-dc-product) — s'il apparaît ainsi, il est retiré du panier plutôt que laissé achetable.
		temp.querySelectorAll(CART_CONFIG.selectors.misplacedGiftProduct).forEach(el => {
			const variantId = el.dataset.cartItemId;
			if (!variantId) return;
			updates[variantId] = 0;
			cartEmit('gift-product-misplaced-removed', { variantId });
			window.log('warn', 'Cart', 'Gift-metafield product found as regular cart item, removing', { variantId });
		});

		if (Object.keys(updates).length > 0) {
			try {
				// Shopify cart/update.js expects updates object and optional sections
				const requestBody = { updates };

				if (sectionId) {
					requestBody.sections = sectionId;
				}

				await CartAPI.request('POST', 'cart/update.js', requestBody);
				cartEmit('free-products-updated', { updates });
			} catch (error) {
				cartEmit('free-products-error', { error: error.message });
			}
		}

		return data;
	}
};

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
// Keyed per line index: window.debounce shares one timer across all calls,
// so debouncing lineIndex 1 would cancel a pending sync for lineIndex 2 —
// only the last-touched line would ever reach the server (cf. cart not
// updating after removing/zeroing several items in quick succession).
const debouncedUpdaters = {};
const debouncedUpdate = (lineIndex) => {
	if (!debouncedUpdaters[lineIndex]) {
		debouncedUpdaters[lineIndex] = window.debounce(() => {
			const quantity = Cart.getQuantity(lineIndex);
			Cart.updateQuantity(lineIndex, quantity);
		}, CART_CONFIG.debounceDelay);
	}
	debouncedUpdaters[lineIndex]();
};

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: 'increase'
	};

	if (!cartEmit('before-quantity-increase', eventData, true)) return;

	if (newQty > max) {
		cartEmit('quantity-limit-reached', eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-increased', { ...eventData, newQty });
	debouncedUpdate(lineIndex);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: 'decrease'
	};

	if (!cartEmit('before-quantity-decrease', eventData, true)) return;

	if (newQty < 1) {
		// If quantity would be less than 1, remove the item completely
		const removeEventData = {
			...itemData,
			oldQty: current,
			action: 'remove'
		};

		if (!cartEmit('before-item-remove', removeEventData, true)) return;

		Cart.setQuantity(lineIndex, 0);
		cartEmit('item-removed', removeEventData);
		debouncedUpdate(lineIndex);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-decreased', eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: 'destroy'
	};

	if (!cartEmit('before-item-remove', eventData, true)) return;

	Cart.setQuantity(lineIndex, 0);
	cartEmit('item-removed', eventData);
	debouncedUpdate(lineIndex);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
window.mmOpenCart = (triggerElement) => Cart.open(triggerElement);
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });

// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Close cart functionality
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-cart-close]')) {
			e.preventDefault();
			Cart.close();
		}

		// Checkout button loading
		if (e.target.closest('[data-checkout-btn]')) {
			const btn = e.target.closest('[data-checkout-btn]');
			btn.classList.add('mm-loading');
			cartEmit('checkout-loading', { element: btn });
		}

		// Discount removal
		const discountButton = e.target.closest('[data-discount-remove]');
		if (discountButton) {
			e.preventDefault();
			const discountTitle = discountButton.getAttribute('data-discount-title');
			if (discountTitle) {
				window.removeDiscount(discountTitle);
			}
		}
	});
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
window.initAddToCartForms = () => {
	document.querySelectorAll('[data-type="add-to-cart-form"]:not([data-cart-handler-attached])').forEach(form => {
		form.setAttribute('data-cart-handler-attached', 'true');
		form.addEventListener('submit', async (e) => {
			e.preventDefault();

			const submitButton = form.querySelector('[type="submit"]');
			const formData = new FormData(form);

			if (!formData.get('id')) {
				window.log('error', 'Cart', 'No variant ID found in form', { form });
				return;
			}

			// Add loading state
			if (submitButton) {
				submitButton.classList.add('mm-loading');
				submitButton.disabled = true;
			}

			// Extract all form data including properties and options
			const extractedData = {
				variantId: formData.get('id'),
				quantity: parseInt(formData.get('quantity') || 1, 10),
				properties: {},
				options: {},
				formEntries: {}
			};

			// Collect all form entries
			for (const [key, value] of formData.entries()) {
				extractedData.formEntries[key] = value;

				// Extract properties (custom fields)
				if (key.startsWith('properties[')) {
					const propName = key.match(/properties\[(.+)\]/)?.[1];
					if (propName && value) {
						extractedData.properties[propName] = value;
					}
				}

				// Extract options (variant selections like size, color)
				if (key.startsWith('option')) {
					extractedData.options[key] = value;
				}
			}

			cartEmit('add-to-cart-start', extractedData);

			try {
				const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

				// Add sections for cart update if available
				if (sectionId) {
					formData.append('sections', sectionId);
				}

				const response = await CartAPI.request('POST', 'cart/add.js', formData);
				cartEmit('add-to-cart-success', {
					...extractedData,
					response: response
				});

				// Process free products after adding to cart
				await Cart.processFreeProducts(response);

				// Auto-open cart after successful add
				Cart.open();

			} catch (error) {
				cartEmit('add-to-cart-error', {
					...extractedData,
					error: error.message
				});
				window.log('error', 'Cart', 'Add to cart failed', {
					error: error.message,
					...extractedData
				});
			} finally {
				// Remove loading state
				if (submitButton) {
					submitButton.classList.remove('mm-loading');
					submitButton.disabled = false;
				}
			}
		});
	});
};

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
window.initCartQuantityListeners = function initCartQuantityListeners() {
	// Cart decrease buttons
	document.querySelectorAll('[data-cart-decrease]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartDecrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDecreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart increase buttons
	document.querySelectorAll('[data-cart-increase]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartIncrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmIncreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart remove buttons (delete item)
	document.querySelectorAll('[data-cart-remove]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartRemove;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			if (btn.closest('.mm-dc-free-product') && variantId) {
				DeclinedFreeProducts.add(variantId);
			}

			window.mmDestroyProductFromCart(lineIndex);
			if (priceContainer) {
				window.LoaderManager.loadThis(priceContainer);
			} else {
				// For free products, load the button itself
				window.LoaderManager.loadThis(btn);
			}
		});
	});
}

/*********************************************************************************************
 * 💰 PROMO CODE MANAGEMENT
 */
function initPromoCode() {
	const applyBtn = document.querySelector('[data-action="apply-discount"]');
	const discountInput = document.querySelector('input[data-name="discount"]');

	window.log('info', 'Cart', 'Initializing promo code', { applyBtn: !!applyBtn, discountInput: !!discountInput });

	if (applyBtn && discountInput && !applyBtn.dataset.promoHandlerAttached) {
		applyBtn.dataset.promoHandlerAttached = 'true';
		applyBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			window.log('info', 'Cart', 'Apply discount button clicked');
			applyDiscount();
		});
		discountInput.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				window.log('info', 'Cart', 'Enter key pressed in discount input');
				applyDiscount();
			}
		});
		// Hide error when user starts typing
		discountInput.addEventListener('input', () => {
			hidePromoError();
		});
		window.log('info', 'Cart', 'Promo code listeners attached successfully');
	}
}

// Codes réellement appliqués par Shopify (produit ou commande), hors remises automatiques
function getAppliedDiscountCodes(cart) {
	return (cart.discount_codes || []).filter(d => d.applicable);
}

async function applyDiscount() {
	window.log('info', 'Cart', 'applyDiscount function called');

	const input = document.querySelector('input[data-name="discount"]');
	const button = document.querySelector('[data-action="apply-discount"]');

	if (!input || !button) {
		window.log('error', 'Cart', 'Input or button not found', { input: !!input, button: !!button });
		return;
	}

	const code = input.value.trim();
	window.log('info', 'Cart', 'Discount code entered', { code });

	if (!code) {
		window.log('warn', 'Cart', 'Empty discount code');
		return;
	}

	// Disable button and show loading state
	button.disabled = true;
	button.classList.add('mm-loading');

	try {
		// Get cart state before applying discount
		window.log('info', 'Cart', 'Getting cart state before discount...');
		const cartBefore = await CartAPI.request('GET', 'cart.js');
		// discount_codes couvre les codes produit (line-level) ET commande (cart-level)
		const discountsBefore = getAppliedDiscountCodes(cartBefore);
		window.log('info', 'Cart', 'Cart before', {
			total: cartBefore.total_price,
			discounts: discountsBefore.length,
			discountsList: discountsBefore
		});

		// Check if this exact code is already applied
		const codeAlreadyApplied = discountsBefore.some(d => d.code.toLowerCase() === code.toLowerCase());

		if (codeAlreadyApplied) {
			showPromoError(input.dataset.errorAlreadyApplied || 'Code already applied');
			window.log('info', 'Cart', 'This code is already applied', { code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if another discount is already applied (Shopify allows only one)
		if (discountsBefore.length > 0) {
			const existingDiscount = discountsBefore[0].code;
			showPromoError(`${input.dataset.errorAlreadyApplied || 'Code already applied'} (${existingDiscount})`);
			window.log('info', 'Cart', 'Another discount already applied', { existing: existingDiscount, attempted: code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if cart is empty
		if (cartBefore.item_count === 0) {
			showPromoError(input.dataset.errorEmptyCart || 'Cart is empty');
			window.log('info', 'Cart', 'Cart is empty');
			input.value = ''; // Clear invalid code
			return;
		}

		// Apply discount via cart/update.js API
		window.log('info', 'Cart', 'Applying discount via API...', { code });

		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		const updatePayload = {
			discount: code
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Cart updated', { updateData });

		// Verify if discount was applied
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const codeApplied = getAppliedDiscountCodes(cartAfter).some(d => d.code.toLowerCase() === code.toLowerCase());

		if (!codeApplied) {
			// Discount was not applied - code is invalid
			window.log('warn', 'Cart', 'Discount was not applied by Shopify', { code });

			// IMPORTANT: Force removal of invalid code from cart
			const cleanupPayload = {
				discount: '' // Empty string removes the discount
			};

			if (sectionId) {
				cleanupPayload.sections = sectionId;
			}

			try {
				await fetch(window.Shopify.routes.root + 'cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(cleanupPayload)
				});
				window.log('info', 'Cart', 'Invalid code cleaned from cart', { code });
			} catch (cleanupError) {
				window.log('error', 'Cart', 'Failed to clean invalid code', { error: cleanupError.message });
			}

			showPromoError(input.dataset.errorNotApplicable || 'Discount code not applicable');
			input.value = ''; // Clear invalid code
			return;
		}

		// Discount applied successfully, refresh UI
		window.log('info', 'Cart', 'Discount applied successfully');

		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
			await Cart.processFreeProducts(updateData);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
					await Cart.processFreeProducts(cartData);
				}
			}
		}

		// Clear input
		input.value = '';

		// Emit success event
		cartEmit('discount-applied', { code });
		window.log('info', 'Cart', 'Discount applied and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to apply discount', { error: error.message, stack: error.stack });
		showPromoError(input.dataset.errorText || 'Error applying discount code');
		input.value = ''; // Clear invalid code on error
	} finally {
		// Reset button state
		button.disabled = false;
		button.classList.remove('mm-loading');
	}
}

function showPromoError(message) {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		const textEl = errorElement.querySelector('.mm-dc-promo-error-text');
		if (textEl) textEl.textContent = message;
		else errorElement.textContent = message;
		errorElement.classList.remove('mm-none');
	}

	if (input) {
		input.classList.add('error');
	}

	// Auto-hide after 5 seconds
	setTimeout(() => {
		hidePromoError();
	}, 5000);
}

function hidePromoError() {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.classList.add('mm-none');
		const textEl = errorElement.querySelector('.mm-dc-promo-error-text');
		if (textEl) textEl.textContent = '';
	}

	if (input) {
		input.classList.remove('error');
	}
}

window.removeDiscount = async function(code) {
	window.log('info', 'Cart', 'Removing discount', { code });

	const discountButton = document.querySelector(`[data-discount-code="${code.toLowerCase()}"] [data-discount-remove]`);

	if (discountButton) {
		// Add loading state
		discountButton.disabled = true;
		discountButton.style.opacity = '0.5';
	}

	try {
		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Remove discount via cart/update.js API with empty discount parameter
		window.log('info', 'Cart', 'Removing discount via API...', { code });

		const updatePayload = {
			discount: ''  // Empty string removes the discount
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Discount removal response', { updateData });

		// Verify discount was removed
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const discountStillPresent = getAppliedDiscountCodes(cartAfter).some(d => d.code.toLowerCase() === code.toLowerCase());

		if (discountStillPresent) {
			window.log('error', 'Cart', 'Discount was not removed', { code });
			throw new Error('Discount removal failed');
		}

		window.log('info', 'Cart', 'Discount removed successfully', { code });

		// Refresh UI
		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
			await Cart.processFreeProducts(updateData);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
					await Cart.processFreeProducts(cartData);
				}
			}
		}

		// Emit success event
		cartEmit('discount-removed', { code });
		window.log('info', 'Cart', 'Discount removed and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to remove discount', { error: error.message, stack: error.stack });

		// Reset button state on error
		if (discountButton) {
			discountButton.disabled = false;
			discountButton.style.opacity = '1';
		}
	}
};

/*********************************************************************************************
 * 🔀 CROSS-SELL RECOMMENDATIONS
 */
const CrossSellRecommendationsManager = {
	_fetched: false,

	init() {
		// Deferred so the cart.json fetch + JSON parsing it kicks off don't land on the
		// main thread while the drawer's opening transform transition is still running —
		// resolving mid-animation was dropping frames and made the slide-in look janky.
		const deferredLoad = () => {
			if ('requestIdleCallback' in window) {
				requestIdleCallback(() => this.load(), { timeout: 700 });
			} else {
				setTimeout(() => this.load(), 500);
			}
		};
		document.addEventListener('cart:opened', deferredLoad);
		document.addEventListener('cart:refreshed', () => { this._fetched = false; deferredLoad(); });
	},

	_mountSplide(container) {
		const splideEl = container?.querySelector('.splide');
		if (!splideEl || !window.Splide) return;
		if (splideEl._splide) return;
		// Attendre la fin de la transition du drawer avant de monter (largeur calculable).
		// Ne pas lire getComputedStyle ici : ça force un style recalc synchrone en plein
		// milieu de la transition CSS transform du drawer (qui tourne sinon uniquement sur
		// le compositor thread), ce qui casse la fluidité de l'animation d'ouverture.
		const drawerEl = document.querySelector('.mm-drawer-cart');
		const wrapperEl = document.querySelector('.mm-drawer-cart-wrapper');
		const doMount = () => {
			if (splideEl._splide) return;
			try {
				splideEl._splide = new window.Splide(splideEl).mount();
			} catch (e) {
				window.log('warn', 'CrossSell', 'Splide mount failed', { error: e.message });
			}
		};
		if (drawerEl && wrapperEl?.classList.contains('mm-dc-is-open')) {
			drawerEl.addEventListener('transitionend', function onEnd(e) {
				if (e.propertyName !== 'transform') return;
				drawerEl.removeEventListener('transitionend', onEnd);
				doMount();
			});
		} else {
			doMount();
		}
	},

	async load() {
		if (this._fetched) return;

		const container = document.querySelector('[data-cart-cross-sell]');
		if (!container) return;

		// Si l'attribut est absent, use_recommendations est désactivé — monter Splide sur les slides manuels et sortir
		if (!('recommendationsIds' in container.dataset)) {
			this._mountSplide(container);
			return;
		}

		try {
			// Lire les product IDs depuis l'API cart (toujours à jour, même après updates AJAX)
			const cartData = await fetch(`${window.Shopify.routes.root}cart.json`).then(r => r.json());
			const productIds = (cartData.items || []).map(i => String(i.product_id)).filter(Boolean);

			// Panier vide → slides manuels uniquement
			if (!productIds.length) {
				this._mountSplide(container);
				return;
			}

			// Panier non vide → recommendations basées sur les produits du panier
			const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${productIds[0]}&limit=8&intent=related`;
			const response = await fetch(url);
			const data = await response.json();

			const products = (data.products || []).filter(p => !productIds.includes(String(p.id)));

			if (!products.length) {
				this._mountSplide(container);
				return;
			}

			this._renderSlides(container, products);
			this._fetched = true;
		} catch (error) {
			window.log('warn', 'CrossSell', 'Recommendations fetch failed — using manual fallback', { error: error.message });
		}
	},

	_renderSizes(variants) {
		if (variants.length <= 1) {
			return `<p class="mm-dc-cross-product-one-size">Taille unique</p>`;
		}
		const chips = variants.slice(0, 5).map(v => {
			const label = v.title.split(' / ')[0].substring(0, 4);
			const cls = v.available ? 'mm-dc-cross-size-chip' : 'mm-dc-cross-size-chip mm-dc-cross-size-chip--unavailable';
			return `<span class="${cls}">${label}</span>`;
		}).join('');
		return `<div class="mm-dc-cross-product-sizes mm-flex mm-align-center mm-gap-1 mm-flex-wrap">${chips}</div>`;
	},

	_renderSlides(container, products) {
		const list = container.querySelector('[data-cross-sell-list]');
		if (!list) return;

		const ctaText = container.dataset.ctaText || 'Découvrir';

		const slides = products.map(product => {
			const imgSrc = product.featured_image ? product.featured_image + '&width=300' : '';
			const title = product.title.length > 30 ? product.title.substring(0, 30) + '…' : product.title;
			const sizesHTML = this._renderSizes(product.variants || []);
			const url = product.url.split('?')[0];

			return `<li class="splide__slide">
				<div class="mm-dc-cross-product">
					<a href="${url}" class="mm-dc-cross-product-image" aria-label="${product.title}">
						<img src="${imgSrc}" alt="${product.title}" loading="lazy" width="300">
					</a>
					<div class="mm-dc-cross-product-content">
						<p class="mm-dc-cross-product-title">${title}</p>
						${sizesHTML}
					</div>
					<a href="${url}" class="mm-btn mm-btn-primary mm-bg-white mm-btn-xsmall mm-dc-cross-product-cta mm-text-sm mm-black-text">
						<span class="mm-btn__mask">
							<span class="mm-btn__texts">
								<span class="mm-btn__text">
									<span data-btn-label>${ctaText}</span>
								</span>
								<span class="mm-btn__text" aria-hidden="true">
									<span data-btn-label>${ctaText}</span>
								</span>
							</span>
						</span>
						<span class="loader"></span>
					</a>
				</div>
			</li>`;
		}).join('');

		list.querySelectorAll('[data-manual-slide]').forEach(el => el.remove());
		list.insertAdjacentHTML('afterbegin', slides);

		const splideEl = container.querySelector('.splide');
		if (splideEl && typeof window.Splide !== 'undefined') {
			if (splideEl._splide) {
				splideEl._splide.destroy();
				delete splideEl._splide;
			}
			try {
				splideEl._splide = new window.Splide(splideEl).mount();
			} catch (error) {
				window.log('warn', 'CrossSell', 'Splide remount failed', { error: error.message });
			}
		}
	},
};

CrossSellRecommendationsManager.init();

/*********************************************************************************************
 * 💱 CURRENCY RATE SYNC
 * Liquid exposes no exchange rate: MM-Cart.liquid converts its shop-currency thresholds with the
 * hidden cart attribute _mm_currency_rate ("ISO:rate"), kept in sync with Shopify.currency here.
 * Returns the cart/update.js response (with sections) when an update was needed, null otherwise.
 */
async function syncCurrencyRate(cart, sectionId) {
	const { active, rate } = window.Shopify?.currency || {};
	// Shop currency (rate 1): Liquid needs no attribute
	if (!active || !rate || parseFloat(rate) === 1) return null;

	const value = `${active}:${rate}`;
	if (cart.attributes?._mm_currency_rate === value) return null;

	try {
		const data = await CartAPI.request('POST', 'cart/update.js', { attributes: { _mm_currency_rate: value }, sections: sectionId });
		cartEmit('currency-rate-synced', { value });
		return data;
	} catch (error) {
		window.log('error', 'Cart', 'Failed to sync currency rate', { error: error.message });
		return null;
	}
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	cartEmit('initializing');
	window.initCartQuantityListeners();
	initPromoCode();

	Cart.get()
		.then(async data => {
			cartEmit('initialized', { itemCount: data.item_count, totalPrice: data.total_price });
			initCartEventListeners();
			window.initAddToCartForms();

			// Sync free gifts with the current cart state (e.g. threshold already crossed before this page load)
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
			if (sectionId) {
				const sectionData = await syncCurrencyRate(data, sectionId)
					|| await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				await Cart.processFreeProducts(sectionData);
			}
		})
		.catch(error => {
			window.log('error', 'Cart', 'Cart initialization failed', { error: error.message });
			cartEmit('initialization-error', { error: error.message });
		});
});

// Reset stale loading states (e.g. checkout button) when the page is restored from bfcache
window.addEventListener('pageshow', (e) => {
	if (!e.persisted) return;
	window.getElements('.mm-loading').forEach(el => el.classList.remove('mm-loading'));
});
